import React, { useCallback, useEffect, useState } from "react";
import { ScreeningResultsControlled } from "./card-controlled";
import {
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  BACKGROUND_CHECK_STATUS_SF_ENDPOINT,
  buildUrl,
  DRUG_TEST_STATUS_ENDPOINT,
} from "../../config/urls";
import { getCandidateData } from "../../utility/candidate-data";
import { getCaasInfo } from "../../service/get-caas-info";
import { fetchWithTimeout } from "../../helpers/fetcher";
import { getSettings, Stage } from "../../helpers/get-settings";
import { I9Response } from "../i9-task/i9-controlled";
import { getApplicationManageData } from "../../utility/application-data";
import {
  isCustomerService,
  isTaskEnabledByAppConfig,
} from "../../utility/app-config-helper";
import {
  BGC_CHECK_IN_SCREENING_TASK_ID,
  DT_IN_SCREENING_CHECK_TASK_ID,
  I9_TASK_ID,
} from "../../config/appConfigConstants";

interface ScreeningResultsUncontrolledProps {
  applicationId: string;
}

export interface ContingencyResponse {
  status?: string;
}

export const ScreeningResultsUncontrolled = ({
  applicationId,
}: ScreeningResultsUncontrolledProps) => {
  const [i9Response, setI9Response] = useState<I9Response>();
  const [bgcResponse, setBgcResponse] = useState<ContingencyResponse>();
  const [dtResponse, setDtResponse] = useState<ContingencyResponse>();
  const [stage, setStage] = useState<Stage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  const requestData = useCallback(async () => {
    setLoading(true);

    try {
      const { applicationState } = await getApplicationManageData(
        applicationId
      );
      const { candidateId, sfCandidateId } = await getCandidateData(
        applicationId
      );

      if (!candidateId || !sfCandidateId || !applicationState) {
        setError(true);
      }

      const validStates = [
        "CONTINGENCIES_PENDING",
        "START_DATE_CONFIRMATION_PENDING",
        "READY_TO_HIRE",
        "HIRE_COMPLETE",
      ];
      if (applicationState && !validStates.includes(applicationState)) {
        setError(true);
      }

      const bgcURL = buildUrl(
        isCustomerService(applicationId)
          ? BACKGROUND_CHECK_STATUS_SF_ENDPOINT
          : BACKGROUND_CHECK_STATUS_ENDPOINT,
        {
          candidateId: candidateId,
          applicationId,
        }
      );

      const dtURL = buildUrl(DRUG_TEST_STATUS_ENDPOINT, {
        sfCandidateId: sfCandidateId,
        applicationId,
      });

      if (isTaskEnabledByAppConfig(I9_TASK_ID, applicationId)) {
        setI9Response(await getCaasInfo(candidateId));
      }

      if (
        isTaskEnabledByAppConfig(BGC_CHECK_IN_SCREENING_TASK_ID, applicationId)
      ) {
        setBgcResponse(await fetchWithTimeout(bgcURL));
      }

      if (
        isTaskEnabledByAppConfig(DT_IN_SCREENING_CHECK_TASK_ID, applicationId)
      ) {
        setDtResponse(await fetchWithTimeout(dtURL));
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestData();
    getSettings().then((settings) => setStage(settings.stage));
  }, []);

  const property = {
    dtResponse: dtResponse,
    bgcResponse: bgcResponse,
    i9Response: i9Response,
    loading: loading,
    error: error,
    stage: stage,
  };

  return <ScreeningResultsControlled property={property} />;
};
